import { useState } from 'react'

import { SimpleTable, TableSkeleton, TableHeader, DrilldownCell, Alert, AssetDetailModal } from '@/components/ui'
import { useSkeletonOnConfigChange } from '@/hooks'
import useCompany from '@/hooks/useCompany'
import { validateDateRangeWithin13Months } from '@/insights/common/formatUtils'
import { buildQuery } from '@/insights/common/queryUtils'
import Caption, { addDateToCaptionFields } from '@/insights/components/Caption'
import { DateOptions } from '@/insights/components/CustomSingleSelect'
import { getSeverityLabel, severityCodes, SeverityNames } from '@/insights/components/SeverityDropdown'
import { createSeverityHeaders } from '@/insights/components/SeverityHeaderBuilder'
import {
  ApiService,
  InsightConfigurationType,
  type InsightDefinition,
  InsightParametersType,
  InsightStatusCode,
  isInsightStatus,
} from '@/types/dashboard-types'
import { formatNumberLocale } from '@/utils'
import { downloadCsv } from '@/utils/csvExport'

interface AlertCountByAssetParams extends InsightParametersType {
  companyId?: number
  severity?: string[]
  geofenceIds?: string[]
  geofenceCategoryIds?: string[]
  alertConfigIds?: string
  date: string
  from?: string
  to?: string
  top: number
}

interface AlertCountByAssetData {
  assetId: number
  assetName: string
  critical: number
  warning: number
  informational: number
  total: number
  companyName: string
  companyId: number
}

const AlertCountByAsset: InsightDefinition<AlertCountByAssetParams, AlertCountByAssetData[]> = {
  active: true,
  slug: 'alert/count-by-asset',
  category: 'alert',
  name: 'Alert Count by Asset',
  description: 'Display alert counts by asset',
  defaultWidth: 'medium',
  defaultAspectRatio: 16 / 9,

  configuration: {
    transform: ({ parameters, field }) => {
      if (field === 'severity') return { ...parameters, alertConfigIds: undefined }
      if (field === 'geofenceIds') {
        return { ...parameters, geofenceCategoryIds: undefined }
      }
      if (field === 'geofenceCategoryIds') {
        return { ...parameters, geofenceIds: undefined }
      }
      if (field === 'alertConfigIds') return { ...parameters, severity: undefined }
      if (field === 'date' && parameters.date !== 'custom') {
        return { ...parameters, from: undefined, to: undefined }
      }
      return parameters
    },
    controls: [
      { id: 'companyId', control: 'CompanyDropdown', required: true },
      { id: 'note', control: 'Label', label: 'Choose either Severity or Alert Name' },
      { id: 'alertConfigIds', control: 'AlertNameDropdown', required: false },
      { id: 'severity', control: 'SeverityDropdown' },
      { id: 'note', control: 'Label', label: 'Choose either Geofence or Geofence Category' },
      { id: 'geofenceIds', control: 'GeofenceDropdown', required: false },
      { id: 'geofenceCategoryIds', control: 'GeofenceCategoryDropdown', required: false },
      {
        id: 'date',
        control: 'CustomSingleSelect',
        options: DateOptions,
      },
      { id: 'dateRange', control: 'DateTimeRange', dependsOn: 'date', dependsOnValue: 'custom' },
      {
        id: 'top',
        control: 'CustomSingleSelect',
        options: [
          { label: 'All', value: '' },
          { label: 'Top 5', value: 'top5' },
          { label: 'Top 10', value: 'top10' },
          { label: 'Top 20', value: 'top20' },
        ],
      },
    ],

    isValid: (configuration: AlertCountByAssetParams) => {
      const hasSeverity = configuration.severity && configuration.severity.length > 0
      const hasAlert = configuration.alertConfigIds && configuration.alertConfigIds.length > 0

      if (configuration.date === 'custom') {
        if (configuration.from && configuration.to) {
          const fromDate = new Date(configuration.from)
          const toDate = new Date(configuration.to)
          if (fromDate > toDate) {
            return ' Start date must be before end date'
          }
        }
        const dateValidation = validateDateRangeWithin13Months(configuration.from, configuration.to)
        if (dateValidation !== true) {
          return dateValidation
        }
      }
      if (configuration?.companyId) {
        if (hasSeverity !== hasAlert) {
          return true
        }
        if (!hasSeverity && !hasAlert) {
          return 'Alert Name or Severity is required'
        }
        return 'Choose either Alert Name or Severity'
      }
      return 'Company is required.'
    },
  },

  load: async (api: ApiService, parameters: AlertCountByAssetParams) => {
    const path = `/insights/alerts/count-by-asset?${buildQuery(parameters)}`

    const response = await api.get<AlertCountByAssetData[]>({
      path,
      origin: 'AlertCountByAsset#loader',
    })
    if (response.error) {
      return { code: InsightStatusCode.ERROR, messge: response.error }
    }
    if (!response.data) {
      return { code: InsightStatusCode.NO_DATA }
    }
    return response.data
  },

  csv: async (api: ApiService, parameters: AlertCountByAssetParams, title: string) => {
    const path = '/insights/alerts/count-by-asset'
    await downloadCsv(api, path, parameters, title)
  },

  Display: ({ status, configuration, data }) => {
    const [selectedAsset, setSelectedAsset] = useState<number | undefined>()
    const showSkeleton = useSkeletonOnConfigChange({ status, data, configuration })
    const parameters = configuration.parameters as AlertCountByAssetParams
    if (status.code === InsightStatusCode.NOT_CONFIGURED) {
      return <Alert type="info" message={status.message || 'Insight has not been configured'} />
    }

    if (showSkeleton) {
      // This could be handled by the framework by just returning the
      // same status. But we should make the skeleton match the final
      // table result if possible.
      return <TableSkeleton columns={2 + (parameters.severity?.length || 3)} withHeader rowHeight={35} />
    }
    if (status.code !== InsightStatusCode.DISPLAYING && status.code !== InsightStatusCode.LOADING) {
      // Wait until we're ready to display before displaying
      // Framework will handle the other states like error and no data.
      // We could handle no data here with a custom message like
      // "No alerts found for severity critical".
      return null
    }
    if (isInsightStatus(data)) {
      return null
    }
    if (!data?.length) {
      // Probably would never get here, but keeps typescript happy.
      return null
    }
    const severities = (
      parameters.severity?.length
        ? parameters.severity.map((s) => getSeverityLabel(String(s)).toLowerCase())
        : ['critical', 'warning', 'informational']
    ) as SeverityNames[]

    const sortedDisplayData = [...data].sort((a, b) => {
      const aValue = Number(a.total || 0)
      const bValue = Number(b.total || 0)

      return bValue - aValue
    })

    const isParentCompany = data.some((d) => d.companyId !== parameters.companyId)
    // Create table body for the new SimpleTable format
    const body = sortedDisplayData?.map((item) => {
      const result: Record<string, string | DrilldownCell | React.ReactNode> = {
        ...(isParentCompany ? { Company: <span className="flex pl-1">{item.companyName}</span> } : {}),
        Asset: (
          <button onClick={() => setSelectedAsset(item.assetId)} className="flex pl-1 text-blue-500 cursor-pointer">
            {item.assetName}
          </button>
        ),
      }
      severities.forEach((sev) => {
        const code = severityCodes.find((s) => s.label.toLowerCase() === sev)?.value

        const ddConfig: InsightConfigurationType = {
          ...configuration,
          slug: 'alert/alerts-detail',
          title: `${configuration.title}: Details`,
          parameters: {
            ...parameters,
            displayFormat: 'table',
            severity: code,
            assetIds: item.assetId,
            isDrilldown: true,
          },
        }
        result[sev] =
          Number(item[sev]) > 0 ? (
            {
              value: formatNumberLocale(Number(item[sev])),
              className: 'block pr-5 w-full text-center',
              configuration: ddConfig,
              id: `${item.assetId}-${sev}`,
            }
          ) : (
            <span className="block pr-5 w-full text-center">{formatNumberLocale(Number(item[sev]))}</span>
          )
      })
      return result
    })

    const sevHeaders = createSeverityHeaders({
      selectedSeverities: severities,
      includeIcons: true,
      includeTotal: false,
    }) as TableHeader[]

    const headers = [...(isParentCompany ? ['Company'] : []), 'Asset', ...sevHeaders]
    const headerClassNames = sevHeaders.reduce((h, s) => Object.assign(h, { [s.key]: 'w-[100px]' }), {})

    return (
      <section className="text-base-content text-base h-full flex flex-col">
        <div className="min-h-0 flex-1">
          <SimpleTable headers={headers} body={body} headerClassNames={headerClassNames} />
        </div>
        <AssetDetailModal
          show={selectedAsset !== undefined}
          onHide={() => setSelectedAsset(undefined)}
          assetId={selectedAsset}
          companyId={parameters.companyId}
        />
      </section>
    )
  },

  Caption: ({ configuration, status }) => {
    const { companies } = useCompany()
    if (status.code === InsightStatusCode.ERROR) {
      // Framework will handle this
      return 'Error'
    }
    const fields: Record<string, string> = {}
    const params = configuration.parameters as AlertCountByAssetParams
    if (params.companyId) {
      const company = companies.find((c) => c.id === params.companyId)
      if (company) {
        fields['Company'] = company.name
      }
    }
    if (params.severity?.length) {
      const labels = params.severity
        .map((s) => getSeverityLabel(s))
        .filter((l) => !!l)
        .join(', ')
      fields['Severity'] = labels
    }
    addDateToCaptionFields({ ...params, fields, finite: true })
    if (Object.keys(fields).length) {
      return <Caption fields={fields} />
    }
    return ''
  },

  // Could leave this off and the framework should handle it;
  // this is just an illustration of how to do it.
  Footer: ({ data, status }) => {
    if (status.code === InsightStatusCode.LOADING && data === undefined) {
      return ''
    }
    if (status.code === InsightStatusCode.NO_DATA) {
      return <div>No records found</div>
    }
    const rows = data?.length
    if (!rows) {
      return <div>No records found</div>
    }
    return (
      <div>
        {rows} row{rows === 1 ? '' : 's'} found
      </div>
    )
  },
}

export default AlertCountByAsset
